import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, PoundSterling, Truck, Wallet, Receipt, RefreshCw, TrendingUp, Package } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { formatGBP } from "@/lib/marketplace";

/** Company Ledger dashboard (Iteration 199).
 * Reads /api/admin/ledger/summary — an admin-key-guarded endpoint that
 * aggregates `platform_fee_ledger`. Provides the operator a live view
 * of platform-fee inflows, Royal Mail postage collected vs spent, and
 * a Stripe processing-fee true-up.
 */
const ADMIN_KEY_STORAGE = "mtt.admin.key";

export default function AdminLedger() {
  const nav = useNavigate();
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!adminKey.trim()) { setErr("Admin key required"); return; }
    setLoading(true); setErr("");
    try {
      localStorage.setItem(ADMIN_KEY_STORAGE, adminKey.trim());
      const { data } = await api.get("/admin/ledger/summary", { params: { key: adminKey.trim() } });
      setData(data);
    } catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  };

  useEffect(() => { if (adminKey) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = data?.totals;
  const windows = data?.windows;
  const trueUp = data?.stripe_true_up;
  const rows = data?.recent_rows || [];

  const summaryCards = useMemo(() => (totals ? [
    {
      key: "net",
      label: "Net balance in ledger",
      value: totals.net_available_pennies,
      helper: "Inflows minus label spend",
      icon: <Wallet className="w-5 h-5" />,
      tone: "emerald",
    },
    {
      key: "platform",
      label: "Platform fees (10%)",
      value: totals.platform_fee_pennies,
      helper: `${totals.order_count} orders`,
      icon: <PoundSterling className="w-5 h-5" />,
      tone: "amber",
    },
    {
      key: "postage_in",
      label: "Royal Mail postage collected",
      value: totals.postage_collected_pennies,
      helper: "£3.50 per shipping order",
      icon: <Package className="w-5 h-5" />,
      tone: "blue",
    },
    {
      key: "postage_out",
      label: "Click & Drop label spend",
      value: -totals.postage_spent_pennies,
      helper: `${totals.label_purchase_count} labels bought`,
      icon: <Truck className="w-5 h-5" />,
      tone: "rose",
    },
  ] : []), [totals]);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 py-10">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-3" data-testid="admin-ledger-back">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900">Company Ledger</h1>
        <p className="text-sm text-slate-500 mt-1 mb-8">
          Live view of platform fees, Royal Mail postage collections, and label spend from <code className="font-mono text-xs">platform_fee_ledger</code>.
        </p>

        {/* Admin key gate */}
        <div className="card-base p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="admin-ledger-auth">
          <div className="flex-1 min-w-[220px]">
            <label htmlFor="admin-ledger-key" className="text-xs font-semibold text-slate-700">Admin key</label>
            <input
              id="admin-ledger-key"
              type="password"
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              placeholder="X-Admin-Key"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-mono focus:border-brandTeal focus:ring-2 focus:ring-brandTeal/30 outline-none"
              data-testid="admin-ledger-key-input"
            />
          </div>
          <button onClick={load} disabled={loading} className="btn-primary" data-testid="admin-ledger-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
          <Link to="/admin" className="text-xs text-slate-500 hover:text-slate-800 self-center">Admin home →</Link>
        </div>

        {err && <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="admin-ledger-error">{err}</div>}

        {!data && !loading && !err && (
          <div className="card-base p-8 text-center text-slate-500 text-sm" data-testid="admin-ledger-empty">
            Enter your admin key and hit Refresh to load the live ledger.
          </div>
        )}

        {totals && (
          <>
            {/* Summary tiles */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-testid="admin-ledger-tiles">
              {summaryCards.map((c) => {
                const negative = c.value < 0;
                const value = negative ? -c.value : c.value;
                return (
                  <div key={c.key} className="card-base p-5" data-testid={`admin-ledger-tile-${c.key}`}>
                    <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider mb-2 text-${c.tone}-700`}>
                      {c.icon} {c.label}
                    </div>
                    <div className={`text-2xl font-display font-semibold ${negative ? "text-rose-600" : "text-slate-900"}`} data-testid={`admin-ledger-tile-${c.key}-amount`}>
                      {negative ? "-" : ""}{formatGBP(value / 100)}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1">{c.helper}</div>
                  </div>
                );
              })}
            </div>

            {/* Windowed breakdown */}
            <div className="grid gap-4 md:grid-cols-2 mb-8">
              {windows && ["week_to_date", "last_week", "month_to_date", "last_month"].map((k) => {
                const w = windows[k];
                const title = ({
                  week_to_date: "Week to date",
                  last_week: "Last week",
                  month_to_date: "Month to date",
                  last_month: "Last month",
                })[k];
                return (
                  <div key={k} className="card-base p-5" data-testid={`admin-ledger-window-${k}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-brandTeal" /> {title}
                      </div>
                      <div className="text-xs font-mono font-bold text-emerald-700" data-testid={`admin-ledger-window-${k}-net`}>
                        Net {formatGBP(w.net_pennies / 100)}
                      </div>
                    </div>
                    <dl className="text-sm space-y-1.5">
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600">Platform fees</dt>
                        <dd className="font-semibold text-slate-900">{formatGBP(w.platform_fee_pennies / 100)}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600">Postage collected</dt>
                        <dd className="font-semibold text-slate-900">{formatGBP(w.postage_collected_pennies / 100)}</dd>
                      </div>
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600">Label spend</dt>
                        <dd className="font-semibold text-rose-600">-{formatGBP(w.postage_spent_pennies / 100)}</dd>
                      </div>
                    </dl>
                  </div>
                );
              })}
            </div>

            {/* Stripe true-up */}
            {trueUp && (
              <div className="card-base p-5 mb-8" data-testid="admin-ledger-stripe-trueup">
                <div className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5 mb-3">
                  <Receipt className="w-4 h-4 text-brandTeal" /> Stripe processing fee true-up
                </div>
                <div className="grid sm:grid-cols-3 gap-3 text-sm">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Estimated</div>
                    <div className="text-lg font-semibold text-slate-900" data-testid="admin-ledger-trueup-est">
                      {formatGBP(trueUp.estimated_pennies / 100)}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Actual (Stripe webhook)</div>
                    <div className="text-lg font-semibold text-slate-900" data-testid="admin-ledger-trueup-actual">
                      {formatGBP(trueUp.actual_pennies / 100)}
                    </div>
                  </div>
                  <div className={`rounded-xl p-3 ${trueUp.diff_pennies < 0 ? "bg-rose-50" : trueUp.diff_pennies > 0 ? "bg-emerald-50" : "bg-slate-50"}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Delta</div>
                    <div className={`text-lg font-semibold ${trueUp.diff_pennies < 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="admin-ledger-trueup-diff">
                      {trueUp.diff_pennies >= 0 ? "+" : ""}{formatGBP(trueUp.diff_pennies / 100)}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-3">Sample size: {trueUp.sample_size} checkouts</p>
              </div>
            )}

            {/* Recent rows */}
            {rows.length > 0 && (
              <div className="card-base overflow-hidden" data-testid="admin-ledger-rows">
                <div className="px-5 py-3 border-b border-slate-100 text-sm font-bold text-slate-800">Recent ledger entries</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-2">Date</th>
                        <th className="text-left px-4 py-2">Kind</th>
                        <th className="text-left px-4 py-2">Status</th>
                        <th className="text-left px-4 py-2">Order</th>
                        <th className="text-right px-4 py-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r._id || i} className="border-t border-slate-100">
                          <td className="px-4 py-2 text-xs text-slate-500 font-mono">
                            {(r.created_at || r.recorded_at || "").slice(0, 19).replace("T", " ")}
                          </td>
                          <td className="px-4 py-2">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-800">
                              {r.kind || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-600">{r.status || "—"}</td>
                          <td className="px-4 py-2 text-xs font-mono text-slate-600 break-all">
                            {r.order_id || r.session_id || "—"}
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-semibold text-slate-900">
                            {formatGBP((r.amount_pennies || r.fee_pennies || 0) / 100)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
